import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Moveable from "react-moveable";
import { useDispatch, useSelector } from "react-redux";
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

import actions from "@/actions";
import { RootState } from "@/reducers/types";
import { Button } from "@/sx-layout/common/Button";
import { Header } from "@/sx-layout/common/Header";
import { useModal } from "@/sx-layout/components/plotmap/hooks";
import { DeleteClipAreaModal } from "@/sx-layout/components/settings/plotPlanMapping/components/DeleteClipAreaModal";
import { PlotPlanDetailClipEditor } from "@/sx-layout/components/settings/plotPlanMapping/components/PlotPlanDetailClipEditor";
import { PlotPlanDetailDisplayButton } from "@/sx-layout/components/settings/plotPlanMapping/components/PlotPlanDetailDisplayButton";
import { PlotPlanDetailSubMenu } from "@/sx-layout/components/settings/plotPlanMapping/components/PlotPlanDetailSubMenu";
import { PlotPlanMappingArea } from "@/sx-layout/components/settings/plotPlanMapping/components/PlotPlanMappingArea";
import { useMoveablePlotPlanDetail } from "@/sx-layout/components/settings/plotPlanMapping/hooks/useMoveablePlotPlanDetail";
import { usePlotPlanMapping } from "@/sx-layout/components/settings/plotPlanMapping/hooks/usePlotPlanMapping";
import { toDisplayRect } from "@/sx-layout/components/settings/plotPlanMapping/util/plotPlanMappingAdapter";
import { usePlotPlans } from "@/sx-layout/components/settings/plotPlanSetting/hooks/usePlotPlans";
import { PlotPlanType } from "@/sx-layout/components/settings/plotPlanSetting/models";
import { useMapState } from "@/sx-layout/hooks/useMapState";
import { usePlotPlanImage } from "@/sx-layout/hooks/usePlotPlanImage";
import { usePlotPlanMappingApi } from "@/sx-layout/hooks/usePlotPlanMappingApi";
import { stripHashFromHexColor } from "@/sx-layout/utils";

// 定数
const MAX_IMAGE_SIZE = 10000;

export const PlotPlanMapping: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [openEditor, setOpenEditor] = React.useState(false);

  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const containerRef = useRef<HTMLDivElement>(null); // コンテンツ全体のコンテナ
  const headerWrapperRef = useRef<HTMLDivElement>(null); // タイトルとボタンの行
  const mapRef = useRef<HTMLDivElement>(null); // 外側のコンテナ
  const imageRef = useRef<HTMLImageElement>(null); // imgタグ

  const [minScale, setMinScale] = useState(0.5);
  const [maxScale, setMaxScale] = useState(5);
  const { mapState, updateMapState } = useMapState(imageRef, transformRef);
  const { scale, mapRatio } = mapState;
  const [isDragging, setIsDragging] = useState(false);
  const [editingDetailId, setEditingDetailId] = useState<number | null>(null);
  const deleteClipAreaModal = useModal();

  const areaRef = useRef<HTMLDivElement>(null);

  const plotPlanId = useSelector((state: RootState) => state.plotPlanMapping.plotPlanId);

  const { mapImageUrl } = usePlotPlanImage(plotPlanId);

  const { plotPlans, refreshPlotPlans } = usePlotPlans();
  useEffect(() => {
    refreshPlotPlans();
  }, []);

  const backToPlotPlanSetting = () => {
    dispatch(actions.layoutApp.changeLayoutMenu("plot_plan"));
    dispatch(actions.plotPlanMapping.setPlotPlanIdForMapping(0));
  };

  const [, forceUpdate] = useState({});
  const setEditingDetailIdWithUpdate = useCallback((id: number | null) => {
    setEditingDetailId(id);
    // areaRef.current が更新されるのを待つために次のレンダリングを走らせる
    setTimeout(() => forceUpdate({}), 0);
  }, []);

  const errorCallback = () => {
    setEditingDetailIdWithUpdate(null);
    setOpenEditor(false);
  };

  const {
    plotPlanDetails,
    createDetail,
    updateDetail,
    updateCoord,
    updateRotation,
    updateScale,
    remove: removePlotPlanDetail,
  } = usePlotPlanMapping(plotPlanId, errorCallback);
  const [opacity, setOpacity] = useState<Map<number, "rect" | "hybrid" | "image">>(new Map());

  const plotPlanMappingApi = usePlotPlanMappingApi();
  const [detailPlotPlanIdsForOtherOverviews, setDetailPlotPlanIdsForOtherOverviews] = useState([]);
  const refreshDetailPlotPlanMapping = async () => {
    const otherOverviewPlotPlans = plotPlans.filter(
      (v) => v.plot_plan_type === PlotPlanType.OVERVIEW && v.plot_plan_id !== plotPlanId
    );
    const list = [];
    for (const overview of otherOverviewPlotPlans) {
      const plotPlanMappingRes = await plotPlanMappingApi.fetchPlotPlanMappings({
        overview_plot_plan_id: overview.plot_plan_id,
      });
      for (const mapping of plotPlanMappingRes.plot_plan_mappings) {
        list.push(mapping.detail_plot_plan_id);
      }
    }
    setDetailPlotPlanIdsForOtherOverviews(list);
  };
  useEffect(() => {
    refreshDetailPlotPlanMapping();
  }, [plotPlans]);
  const existingDetailPlotPlanIds = useMemo(
    () => [...plotPlanDetails.map((v) => v.detail_plot_plan_id), ...detailPlotPlanIdsForOtherOverviews],
    [plotPlanDetails, detailPlotPlanIdsForOtherOverviews]
  );

  const editingPlotPlanMapping = useMemo(() => {
    return plotPlanDetails.find((v) => v.detail_plot_plan_id === editingDetailId);
  }, [plotPlanDetails, editingDetailId]);

  const updateSelectedDetail = useCallback(
    (area: { top: number; left: number; width: number; height: number; rotation?: number }) => {
      if (!editingPlotPlanMapping) return;
      const id = editingPlotPlanMapping.plot_plan_mapping_id;
      const current = toDisplayRect(editingPlotPlanMapping);
      const rotation = area.rotation ?? 0;
      if (area.width !== current.width || area.height !== current.height) {
        // リサイズ時はサイズ（scale）だけでなく座標（x, y）も一緒に更新する。
        // 左方向・上方向へのリサイズや回転を伴う場合、表示矩形の左上座標が変化するため、
        // scale のみを更新すると x, y が据え置かれて矩形がずれてしまう。
        updateScale(
          id,
          { x: area.left, y: area.top },
          {
            scale_x:
              editingPlotPlanMapping.clip_w > 0
                ? area.width / editingPlotPlanMapping.clip_w
                : editingPlotPlanMapping.scale_x,
            scale_y:
              editingPlotPlanMapping.clip_h > 0
                ? area.height / editingPlotPlanMapping.clip_h
                : editingPlotPlanMapping.scale_y,
          },
          editingPlotPlanMapping.timestamp
        );
      } else if (area.left !== current.left || area.top !== current.top) {
        updateCoord(id, { x: area.left, y: area.top }, editingPlotPlanMapping.timestamp);
      } else if (rotation !== current.rotation) {
        updateRotation(id, rotation, editingPlotPlanMapping.timestamp);
      }
    },
    [editingPlotPlanMapping, updateScale, updateCoord, updateRotation]
  );

  const { onDrag, onDragEnd, onDragStart, onResize, onResizeEnd, onResizeStart, onRotate, onRotateEnd, moveableRef } =
    useMoveablePlotPlanDetail({
      selectedDetail: editingPlotPlanMapping
        ? toDisplayRect(editingPlotPlanMapping)
        : { top: 0, left: 0, width: 0, height: 0 },
      updateSelectedDetail,
      areaRef,
      imageRef,
      mapState,
      setIsDragging,
      clipAspectRatio:
        editingPlotPlanMapping && editingPlotPlanMapping.clip_w > 0 && editingPlotPlanMapping.clip_h > 0
          ? editingPlotPlanMapping.clip_w / editingPlotPlanMapping.clip_h
          : undefined,
    });

  // 画像読み込み完了時にスケールや比率を計算
  const handleResizeMap = useCallback(() => {
    if (mapRef.current && transformRef.current && imageRef.current) {
      const mapRect = mapRef.current.getBoundingClientRect();

      // コンテナに収まる最小スケールを設定（縦フィット・横フィットの小さい方）
      const fitHeightScale = mapRect.height / imageRef.current.offsetHeight;
      const fitWidthScale = mapRect.width / imageRef.current.offsetWidth;
      const calculatedMinScale = Math.min(fitHeightScale, fitWidthScale);
      setMinScale(calculatedMinScale);

      // 元の解像度（IMAGE_BASE_WIDTH）に対する現在の表示比率を計算
      updateMapState(transformRef.current.state.scale, transformRef.current.state);

      // 最大ズーム倍率を画像解像度から制限
      setMaxScale(MAX_IMAGE_SIZE / imageRef.current.offsetWidth);

      // Moveableのガイド位置を更新
      if (moveableRef.current) {
        moveableRef.current.updateRect();
      }
    }
  }, [moveableRef]);

  const [magnificationRatio, setMagnificationRatio] = useState(100);
  const initScale = useRef(false);
  useEffect(() => {
    if (!initScale.current && imageRef.current && transformRef.current) {
      initScale.current = true;
      setMagnificationRatio((mapRatio / scale) * 100 * 2);
    }
  }, [scale, mapRatio]);

  const getZoomScaleFromMagnification = (magnification: number) => {
    return magnification / (mapRatio * 2 * 100);
  };

  const getNextZoomMagnification = (currentMagnification: number, direction: "in" | "out") => {
    const step = 100;
    const roundedMagnification = Math.round(currentMagnification);

    if (direction === "in") {
      return Math.ceil((roundedMagnification + 1) / step) * step;
    }

    return Math.floor((roundedMagnification - 1) / step) * step;
  };

  const handleZoomIn = () => {
    if (!transformRef.current || !mapRef.current) return;
    const { state, setTransform } = transformRef.current;

    const currentMagnification = mapRatio * state.scale * 2 * 100;
    const nextMagnification = getNextZoomMagnification(currentMagnification, "in");
    const targetScale = getZoomScaleFromMagnification(nextMagnification);
    const newScale = Math.min(maxScale, Math.max(minScale, targetScale));

    const wrapper = mapRef.current as HTMLDivElement;
    const cx = wrapper.clientWidth / 2;
    const cy = wrapper.clientHeight / 2;
    const oldScale = state.scale;
    const contentX = (cx - state.positionX) / oldScale;
    const contentY = (cy - state.positionY) / oldScale;
    const newPosX = cx - contentX * newScale;
    const newPosY = cy - contentY * newScale;

    setTransform(newPosX, newPosY, newScale, 200);

    setTimeout(() => {
      if (transformRef.current) {
        updateMapState(transformRef.current.state.scale, transformRef.current.state);
        setMagnificationRatio(Math.round(newScale * mapRatio * 2 * 100));
      }
    }, 200);
  };

  const handleZoomOut = () => {
    if (!transformRef.current || !mapRef.current) return;
    const { state, setTransform } = transformRef.current;
    const step = 100;

    const currentMagnification = mapRatio * state.scale * 2 * 100;
    const epsilon = 1e-6;
    const nextMagnification = Math.floor((currentMagnification - epsilon) / step) * step;
    const targetScale = nextMagnification / (mapRatio * 2 * 100);
    const newScale = Math.min(maxScale, Math.max(minScale, targetScale));

    const wrapper = mapRef.current as HTMLDivElement;
    const cx = wrapper.clientWidth / 2;
    const cy = wrapper.clientHeight / 2;
    const oldScale = state.scale;
    const contentX = (cx - state.positionX) / oldScale;
    const contentY = (cy - state.positionY) / oldScale;
    const newPosX = cx - contentX * newScale;
    const newPosY = cy - contentY * newScale;

    setTransform(newPosX, newPosY, newScale, 200);

    setTimeout(() => {
      if (transformRef.current) {
        updateMapState(transformRef.current.state.scale, transformRef.current.state);
        setMagnificationRatio(Math.round(newScale * mapRatio * 2 * 100));
      }
    }, 200);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResizeMap);
    return () => {
      window.removeEventListener("resize", handleResizeMap);
    };
  }, [handleResizeMap]);

  // 画像読み込み完了時にスケールや比率を計算
  const handleImageLoaded = useCallback(() => {
    handleResizeMap();

    // 横長画像（縦が余る）の場合、縦100%になるよう初期表示をズームする
    if (mapRef.current && transformRef.current && imageRef.current) {
      const mapRect = mapRef.current.getBoundingClientRect();
      if (imageRef.current.offsetHeight < mapRect.height) {
        const fitHeightScale = mapRect.height / imageRef.current.offsetHeight;
        const { setTransform } = transformRef.current;
        const scaledWidth = imageRef.current.offsetWidth * fitHeightScale;
        const posX = (mapRect.width - scaledWidth) / 2;
        setTimeout(() => {
          setTransform(posX, 0, fitHeightScale, 0);
        }, 50);
      }
    }
  }, [handleResizeMap]);

  const savePlotPlanDetailFromEditor = async (props: {
    detailPlotPlanId: number;
    color: string;
    clip_y: number;
    clip_x: number;
    clip_w: number;
    clip_h: number;
    uploadfile: Blob;
  }) => {
    const clip = { clip_x: props.clip_x, clip_y: props.clip_y, clip_w: props.clip_w, clip_h: props.clip_h };
    const color = stripHashFromHexColor(props.color);

    if (editingPlotPlanMapping && editingPlotPlanMapping.detail_plot_plan_id === props.detailPlotPlanId) {
      // 既存更新：位置・回転・スケールは維持し、クリップ・色・画像を更新する
      await updateDetail(editingPlotPlanMapping.plot_plan_mapping_id, {
        ...clip,
        color,
        uploadfile: props.uploadfile,
        scale_x: editingPlotPlanMapping.scale_x,
        scale_y: editingPlotPlanMapping.scale_y,
        x: editingPlotPlanMapping.x,
        y: editingPlotPlanMapping.y,
        rotation: editingPlotPlanMapping.rotation,
        timestamp: editingPlotPlanMapping.timestamp,
      });
      setEditingDetailIdWithUpdate(null);
      setOpenEditor(false);
      return;
    }

    // 新規作成：表示サイズをデフォルト算出し、scale を逆算する
    const ratio = props.clip_h / props.clip_w;
    const defaultSize = 100;
    const detailWidth =
      props.clip_w >= props.clip_h ? Math.round(defaultSize * (props.clip_w / props.clip_h)) : defaultSize;
    const detailHeight = Math.round(detailWidth * ratio);
    const top = Math.round(mapState.viewport.top + mapState.viewport.height / 2 - detailHeight / 2);
    const left = Math.round(mapState.viewport.left + mapState.viewport.width / 2 - detailWidth / 2);

    await createDetail({
      detail_plot_plan_id: props.detailPlotPlanId,
      ...clip,
      uploadfile: props.uploadfile,
      scale_x: props.clip_w > 0 ? detailWidth / props.clip_w : 1,
      scale_y: props.clip_h > 0 ? detailHeight / props.clip_h : 1,
      x: left,
      y: top,
      rotation: 0,
      color,
    });
    setEditingDetailIdWithUpdate(props.detailPlotPlanId);
    setOpenEditor(false);
  };

  return (
    <div className="bg-[#f3f2ef] h-screen flex flex-col overflow-hidden">
      <Header />
      <div ref={containerRef} className="px-[32px] flex flex-col grow min-h-0">
        {/* ヘッダ */}
        <div ref={headerWrapperRef} className="flex items-center space-x-8">
          <h2 className="my-[16px] font-bold text-[16px]">{t("plot_plan_mapping_setting")}</h2>
          {/* 詳細プロットプラン追加ボタン */}
          <Button
            className={`flex items-center justify-center`}
            onClick={() => {
              setEditingDetailId(null);
              setOpenEditor(true);
            }}
          >
            <img src="./img/icon-add.svg" className="mr-2 w-[16px]" />
            {t("add_plot_plan_detail")}
          </Button>
          {/* プロットプランに戻るボタン */}
          <Button buttonType="cancel" onClick={backToPlotPlanSetting}>
            {t("back_to_plot_plan_setting")}
          </Button>
          {/* ズームボタン */}
          <div className="flex-grow flex justify-end items-end space-x-4 h-[36px]">
            <button type="button" onClick={handleZoomOut} className="cursor-pointer">
              <img src="./img/icon-zoom-out.svg" className="w-[18px] h-[18px]" />
            </button>
            <span className="w-[40px] text-[13px] text-center whitespace-nowrap">{`${Math.round(
              magnificationRatio
            )}%`}</span>
            <button type="button" onClick={handleZoomIn} className="cursor-pointer">
              <img src="./img/icon-zoom-in.svg" className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>

        {/* マップ表示エリア */}
        <div className="flex flex-col grow mb-4 bg-[#ccc] relative min-h-0">
          <div
            ref={mapRef}
            className="relative grow overflow-hidden flex items-center justify-center border border-solid min-h-0"
          >
            {mapImageUrl ? (
              <TransformWrapper
                key={`transform-${plotPlanId}`}
                ref={transformRef}
                centerZoomedOut={true}
                initialScale={1}
                minScale={minScale}
                maxScale={maxScale}
                pinch={{ disabled: true }}
                panning={{ velocityDisabled: true, disabled: isDragging }}
                doubleClick={{ disabled: true }}
                velocityAnimation={{ disabled: true }}
                alignmentAnimation={{ disabled: true, sizeX: 0, sizeY: 0 }}
                onZoom={(ref) => {
                  updateMapState(ref.state.scale, ref.state);
                  const currentMapRatio = (imageRef.current?.offsetWidth ?? 0) / 2000;
                  setMagnificationRatio(Math.round(ref.state.scale * currentMapRatio * 2 * 100));
                  if (moveableRef.current) {
                    moveableRef.current.updateRect();
                  }
                }}
                onPanning={(ref) => {
                  updateMapState(ref.state.scale, ref.state);
                  if (moveableRef.current) {
                    moveableRef.current.updateRect();
                  }
                }}
              >
                <TransformComponent
                  contentStyle={{ width: "100%" }}
                  wrapperStyle={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                  }}
                >
                  <div className="relative w-full" onClick={() => setEditingDetailIdWithUpdate(null)}>
                    <img
                      ref={imageRef}
                      src={mapImageUrl}
                      alt="Plot Map"
                      className="w-full block"
                      onLoad={handleImageLoaded}
                    />
                    {plotPlanDetails.map((detail) => {
                      const rect = toDisplayRect(detail);
                      return (
                        <div
                          key={detail.detail_plot_plan_id}
                          ref={detail.detail_plot_plan_id === editingDetailId ? areaRef : undefined}
                          className={detail.detail_plot_plan_id === editingDetailId ? "z-10" : ""}
                          style={{
                            position: "absolute",
                            top: rect.top * mapRatio,
                            left: rect.left * mapRatio,
                            width: rect.width * mapRatio,
                            height: rect.height * mapRatio,
                            transform: `rotate(${rect.rotation}deg)`,
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingDetailIdWithUpdate(detail.detail_plot_plan_id);
                          }}
                        >
                          <PlotPlanMappingArea
                            plotPlanMapping={detail}
                            opacity={opacity.get(detail.detail_plot_plan_id)}
                          />
                        </div>
                      );
                    })}
                    {editingPlotPlanMapping && areaRef.current && (
                      <Moveable
                        ref={moveableRef}
                        target={areaRef.current}
                        origin={false}
                        draggable={true}
                        zoom={scale}
                        onDragStart={onDragStart}
                        onDrag={onDrag}
                        onDragEnd={onDragEnd}
                        resizable={true}
                        onResizeStart={onResizeStart}
                        onResize={onResize}
                        onResizeEnd={onResizeEnd}
                        rotatable={true}
                        onRotate={onRotate}
                        onRotateEnd={onRotateEnd}
                      />
                    )}
                    {!isDragging && editingPlotPlanMapping && (
                      <>
                        <PlotPlanDetailSubMenu
                          mapState={mapState}
                          rect={toDisplayRect(editingPlotPlanMapping)}
                          onEdit={(e) => {
                            e.stopPropagation();
                            setOpenEditor(true);
                          }}
                          onDelete={(e) => {
                            e.stopPropagation();
                            deleteClipAreaModal.open();
                          }}
                        />
                        <PlotPlanDetailDisplayButton
                          mapState={mapState}
                          rect={toDisplayRect(editingPlotPlanMapping)}
                          onClick={(e) => {
                            e.stopPropagation();
                            const currentOpacity = opacity.get(editingPlotPlanMapping.detail_plot_plan_id) ?? "rect";
                            setOpacity(
                              new Map(
                                opacity.set(
                                  editingPlotPlanMapping.detail_plot_plan_id,
                                  currentOpacity === "rect" ? "hybrid" : currentOpacity === "hybrid" ? "image" : "rect"
                                )
                              )
                            );
                          }}
                        />
                      </>
                    )}
                  </div>
                </TransformComponent>
              </TransformWrapper>
            ) : (
              <div>{t("loading")}</div>
            )}
          </div>
        </div>
      </div>
      {/* 詳細プロットプラン編集 */}
      {openEditor && (
        <PlotPlanDetailClipEditor
          selected={editingPlotPlanMapping}
          existingDetailPlotPlanIds={existingDetailPlotPlanIds}
          onSave={savePlotPlanDetailFromEditor}
          onClose={() => {
            setOpenEditor(false);
            setEditingDetailIdWithUpdate(null);
          }}
        />
      )}
      {/* エリア詳細削除モーダル */}
      {deleteClipAreaModal.isOpen && (
        <DeleteClipAreaModal
          onClose={() => {
            deleteClipAreaModal.close();
            setEditingDetailIdWithUpdate(null);
          }}
          onSubmit={() => {
            removePlotPlanDetail(editingPlotPlanMapping.plot_plan_mapping_id);
            deleteClipAreaModal.close();
            setEditingDetailIdWithUpdate(null);
          }}
        />
      )}
    </div>
  );
};
